import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useSession } from "@/context/AuthContext";
import api from "@/lib/api";

const KEY = "sema_active_workspace";
const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const { user } = useSession();
  const [workspaces, setWorkspaces] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(KEY) || null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setWorkspaces([]);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/workspaces");
      setWorkspaces(data);
      setActiveId((prev) => {
        const valid = data.find((w) => w.id === prev);
        const next = valid ? prev : data[0]?.id || null;
        if (next) localStorage.setItem(KEY, next);
        return next;
      });
    } catch {
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setActive = (id) => {
    localStorage.setItem(KEY, id);
    setActiveId(id);
  };

  const active = workspaces.find((w) => w.id === activeId) || null;

  return (
    <WorkspaceContext.Provider value={{ workspaces, active, activeId, setActive, refresh, loading }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used within WorkspaceProvider");
  return ctx;
}
